import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
    Modal, Stack, Group, Text, Progress, Loader, ThemeIcon, Button,
} from '@mantine/core';
import { IconCheck, IconAlertTriangle, IconFileDescription } from '@tabler/icons-react';
import { shikoDokumentin } from '../../../../services/fetch-service';

const LABELS = {
    sq: {
        title: 'Shkarkimi i dokumentit',
        steps: [
            'Po lidhemi me shërbimin…',
            'Po përgatitet dokumenti…',
            'Po fshihen të dhënat personale…',
            'Pothuajse gati, ju lutemi prisni…',
        ],
        hint: 'Përpunimi i dokumentit mund të zgjasë disa minuta.',
        done: 'Dokumenti u shkarkua.',
        error: 'Dokumenti nuk mund të shkarkohej. Provoni përsëri.',
        notFound: 'Dokumenti nuk u gjet.',
        suspended: 'Për shkak të mungesës së kapaciteteve për anonimizimin e plotë të të dhënave personale, kërkesa nuk mund të realizohet.',
        close: 'Mbyll',
        retry: 'Provo përsëri',
        cancel: 'Anulo',
    },
    en: {
        title: 'Document download',
        steps: [
            'Connecting to the service…',
            'Preparing the document…',
            'Redacting personal data…',
            'Almost ready, please wait…',
        ],
        hint: 'Processing the document may take a few minutes.',
        done: 'The document was downloaded.',
        error: 'The document could not be downloaded. Please try again.',
        notFound: 'Document not found.',
        suspended: 'Due to the lack of capacities for full anonymization of personal data, the request cannot be processed.',
        close: 'Close',
        retry: 'Try again',
        cancel: 'Cancel',
    },
    sr: {
        title: 'Preuzimanje dokumenta',
        steps: [
            'Povezivanje sa servisom…',
            'Priprema dokumenta…',
            'Uklanjanje ličnih podataka…',
            'Skoro je gotovo, molimo sačekajte…',
        ],
        hint: 'Obrada dokumenta može trajati nekoliko minuta.',
        done: 'Dokument je preuzet.',
        error: 'Dokument nije mogao biti preuzet. Pokušajte ponovo.',
        notFound: 'Dokument nije pronađen.',
        suspended: 'Zbog nedostatka kapaciteta za potpunu anonimizaciju ličnih podataka, zahtev ne može biti realizovan.',
        close: 'Zatvori',
        retry: 'Pokušajte ponovo',
        cancel: 'Otkaži',
    },
};

const STEP_INTERVAL_MS = 3500;
const PROGRESS_TICK_MS = 700;

// Shkarkimi i dokumenteve eshte pezulluar perkohesisht me kerkese te ARBK-se.
// Vendose false per ta rikthyer shkarkimin (asnje ndryshim tjeter nuk nevojitet).
const SHKARKIMI_AKTIV = false;

function filenameFromDisposition(cd) {
    if (!cd) return '';
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    if (star) {
        try {
            return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
        } catch (e) {
            return '';
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    return plain ? plain[1].trim() : '';
}

function pastroPjesen(vlera) {
    return (vlera || '').trim().replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '_');
}

function ndertoEmrin(nui, emri, id) {
    const baza = pastroPjesen(emri) || `dokument-${id}`;
    let paExtension = baza.replace(/\.pdf$/i, '');
    const prefiksi = pastroPjesen(nui);
    if (!prefiksi) return `${paExtension}.pdf`;
    if (paExtension.indexOf(`${prefiksi}_`) === 0) paExtension = paExtension.slice(prefiksi.length + 1);
    return `${prefiksi}_${paExtension}.pdf`;
}

function saveBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);
}

export default function DokumentiModal({
    opened, dokumenti, locale, onClose,
}) {
    const L = LABELS[locale] || LABELS.sq;
    const [status, setStatus] = useState(SHKARKIMI_AKTIV ? 'loading' : 'suspended');
    const [stepIndex, setStepIndex] = useState(0);
    const [percent, setPercent] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const abortRef = useRef(null);

    const shkarko = useCallback(async () => {
        if (!SHKARKIMI_AKTIV) {
            setStatus('suspended');
            return;
        }
        if (!dokumenti || !dokumenti.id) return;

        setStatus('loading');
        setStepIndex(0);
        setPercent(0);
        setErrorMsg('');

        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const res = await shikoDokumentin(
                dokumenti.id,
                controller.signal,
                dokumenti.nui,
                dokumenti.emri,
            );

            if (!res.ok) {
                setStatus('error');
                setErrorMsg(res.status === 404 ? L.notFound : L.error);
                return;
            }

            const blob = await res.blob();
            if (!blob || blob.size === 0) {
                setStatus('error');
                setErrorMsg(L.notFound);
                return;
            }

            const emriBazes = dokumenti.emri
                || filenameFromDisposition(res.headers.get('content-disposition'));

            saveBlob(blob, ndertoEmrin(dokumenti.nui, emriBazes, dokumenti.id));
            setPercent(100);
            setStatus('done');
        } catch (err) {
            if (controller.signal.aborted) return;
            setStatus('error');
            setErrorMsg(L.error);
        }
    }, [dokumenti, L]);

    useEffect(() => {
        if (!opened) return function noop() {};
        shkarko();
        return function cleanup() {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [opened, dokumenti && dokumenti.id]);

    useEffect(() => {
        if (status !== 'loading') return function noop() {};
        const id = setInterval(() => {
            setStepIndex((i) => (i < L.steps.length - 1 ? i + 1 : i));
        }, STEP_INTERVAL_MS);
        return function cleanup() { clearInterval(id); };
    }, [status, L]);

    useEffect(() => {
        if (status !== 'loading') return function noop() {};
        const id = setInterval(() => {
            setPercent((p) => (p >= 92 ? p : p + Math.max(1, Math.round((92 - p) / 12))));
        }, PROGRESS_TICK_MS);
        return function cleanup() { clearInterval(id); };
    }, [status]);

    const mbyll = () => {
        if (abortRef.current) abortRef.current.abort();
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={mbyll}
            title={L.title}
            centered
            closeOnClickOutside={status !== 'loading'}
            closeOnEscape={status !== 'loading'}
            withCloseButton={status !== 'loading'}
        >
            <Stack spacing="md">
                <Group spacing="xs" align="flex-start" noWrap>
                    <ThemeIcon color="blue" variant="light" radius="xl" size="lg">
                        <IconFileDescription size={18} />
                    </ThemeIcon>
                    <Text size="sm" weight={600} style={{ wordBreak: 'break-word' }}>
                        {(dokumenti && dokumenti.emri) || ''}
                    </Text>
                </Group>

                {status === 'suspended' && (
                    <React.Fragment>
                        <Group spacing="xs" align="flex-start" noWrap>
                            <ThemeIcon color="orange" variant="light" radius="xl">
                                <IconAlertTriangle size={18} />
                            </ThemeIcon>
                            <Text size="sm" weight={600} style={{ wordBreak: 'break-word' }}>
                                {L.suspended}
                            </Text>
                        </Group>
                        <Group position="right">
                            <Button variant="light" onClick={mbyll}>{L.close}</Button>
                        </Group>
                    </React.Fragment>
                )}

                {status === 'loading' && (
                    <Stack spacing="xs">
                        <Group spacing="xs" noWrap>
                            <Loader size="sm" />
                            <Text size="sm" weight={600}>{L.steps[stepIndex]}</Text>
                        </Group>
                        <Progress value={percent} striped animate />
                        <Text size="xs" color="dimmed">{L.hint}</Text>
                        <Group position="right">
                            <Button size="xs" variant="subtle" color="gray" onClick={mbyll}>{L.cancel}</Button>
                        </Group>
                    </Stack>
                )}

                {status === 'done' && (
                    <React.Fragment>
                        <Group spacing="xs" noWrap>
                            <ThemeIcon color="teal" radius="xl">
                                <IconCheck size={18} />
                            </ThemeIcon>
                            <Text size="sm" weight={600}>{L.done}</Text>
                        </Group>
                        <Group position="right">
                            <Button variant="light" onClick={mbyll}>{L.close}</Button>
                        </Group>
                    </React.Fragment>
                )}

                {status === 'error' && (
                    <React.Fragment>
                        <Group spacing="xs" align="flex-start" noWrap>
                            <ThemeIcon color="red" radius="xl">
                                <IconAlertTriangle size={18} />
                            </ThemeIcon>
                            <Text size="sm" weight={600}>{errorMsg || L.error}</Text>
                        </Group>
                        <Group position="right" spacing="sm">
                            <Button variant="subtle" onClick={mbyll}>{L.close}</Button>
                            <Button onClick={shkarko}>{L.retry}</Button>
                        </Group>
                    </React.Fragment>
                )}
            </Stack>
        </Modal>
    );
}

DokumentiModal.propTypes = {
    opened: PropTypes.bool,
    dokumenti: PropTypes.object,
    locale: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

DokumentiModal.defaultProps = {
    opened: false,
    dokumenti: null,
    locale: 'sq',
};
